import React from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Image } from "hesus-ui-kit";
import deleteIcon from "@icons/ic-delete-red.svg";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import {
  LineItemsTableWrapper,
  GroupHeaderBar,
  LineItemsGrid,
  LineItemsHeaderRow,
  LineItemsHeaderCell,
  LineItemsRow,
  LineItemsCell,
  LineItemsRowCommentCell,
  HesusServiceLabel,
  HesusServiceSubLabel,
  LineItemInput,
  LineItemSelect,
  RowCommentLink,
  RowCommentEditorWrapper,
  NoLineItemsMessage,
  ErrorText,
} from "../../AddEditPurchaseOrder.styled";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";

const LINE_ITEMS_COLUMNS = "1.4fr 1.3fr 1.1fr 1.1fr 0.6fr 0.8fr 0.7fr 0.5fr";

const PurchaseOrderLineItems = ({ t, lineItems, setLineItems, errors, status }) => {
  const isReadOnly = status !== 0;
  const generalParams = useGetGeneralParameters() || {};
  const { unitList = [] } = generalParams;
  const onLineItemChange = (id, field, value) => {
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter((item) => item.id !== id));
  };

  const toggleComment = (id) => {
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, isCommentOpen: !item.isCommentOpen } : item
      )
    );
  };

  const onCommentChange = (id, value) => {
    setLineItems(
      lineItems.map((item) =>
        item.id === id ? { ...item, comment: value } : item
      )
    );
  };

  const fieldHasError = (value) => !!errors.lineItems && !value;

  const renderRow = (item) => (
    <React.Fragment key={item.id}>
      <LineItemsRow column={LINE_ITEMS_COLUMNS} style={{ borderBottom: "none" }}>
        <LineItemsCell>
          <HesusServiceLabel>
            {item.hesusServiceName || "-"}
          </HesusServiceLabel>
          {item.hesusServicePriceLabel && (
            <HesusServiceSubLabel>
              {item.hesusServicePriceLabel}
            </HesusServiceSubLabel>
          )}
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.label}
            onChange={(e) =>
              onLineItemChange(item.id, "label", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_LABEL")}
            readOnly={isReadOnly}
            $hasError={fieldHasError(item.label)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.product}
            onChange={(e) =>
              onLineItemChange(item.id, "product", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_PRODUCT")}
            readOnly={isReadOnly}
            $hasError={fieldHasError(item.product)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.truckType}
            onChange={(e) =>
              onLineItemChange(item.id, "truckType", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_TRUCK_TYPE")}
            $hasError={fieldHasError(item.truckType)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="number"
            value={item.quantity}
            onChange={(e) =>
              onLineItemChange(
                item.id,
                "quantity",
                e.target.value === "" ? "" : parseFloat(e.target.value)
              )
            }
            $hasError={fieldHasError(item.quantity)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="number"
            value={item.unitPrice}
            onChange={(e) =>
              onLineItemChange(
                item.id,
                "unitPrice",
                e.target.value === "" ? "" : parseFloat(e.target.value)
              )
            }
            $hasError={fieldHasError(item.unitPrice)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemSelect
            value={item.unit}
            onChange={(e) => onLineItemChange(item.id, "unit", e.target.value)}
            $hasError={fieldHasError(item.unit)}
          >
            {unitList.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.label)}
              </option>
            ))}
          </LineItemSelect>
        </LineItemsCell>
        <LineItemsCell>
          <Image
            src={deleteIcon}
            alt={t("GENERIC_ACTION_DELETE")}
            title={t("GENERIC_ACTION_DELETE")}
            className="company-centre-view__image-icon-header-company"
            onClick={() => removeLineItem(item.id)}
          />
        </LineItemsCell>
      </LineItemsRow>
      <LineItemsRowCommentCell column={LINE_ITEMS_COLUMNS}>
        {!item.isCommentOpen && (
          <RowCommentLink onClick={() => toggleComment(item.id)}>
            {t("PURCHASE_ORDER_LINE_ADD_COMMENT")}
          </RowCommentLink>
        )}
        {item.isCommentOpen && (
          <RowCommentEditorWrapper>
            <CKEditorText
              descriptionData={item.comment}
              setDescriptionData={(value) => onCommentChange(item.id, value)}
              minHeight={80}
            />
          </RowCommentEditorWrapper>
        )}
      </LineItemsRowCommentCell>
    </React.Fragment>
  );

  const renderFreeRow = (item) => (
    <React.Fragment key={item.id}>
      <LineItemsRow column={LINE_ITEMS_COLUMNS} style={{ borderBottom: "none" }}>
        <LineItemsCell>
          <HesusServiceLabel>-</HesusServiceLabel>
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.label}
            onChange={(e) =>
              onLineItemChange(item.id, "label", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_LABEL")}
            readOnly={isReadOnly}
            $hasError={fieldHasError(item.label)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.product}
            onChange={(e) =>
              onLineItemChange(item.id, "product", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_PRODUCT")}
            readOnly={isReadOnly}
            $hasError={fieldHasError(item.product)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="text"
            value={item.truckType}
            onChange={(e) =>
              onLineItemChange(item.id, "truckType", e.target.value)
            }
            placeholder={t("PURCHASE_ORDER_LINE_ITEM_TRUCK_TYPE")}
            $hasError={fieldHasError(item.truckType)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="number"
            value={item.quantity}
            onChange={(e) =>
              onLineItemChange(
                item.id,
                "quantity",
                e.target.value === "" ? "" : parseFloat(e.target.value)
              )
            }
            $hasError={fieldHasError(item.quantity)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemInput
            type="number"
            value={item.unitPrice}
            onChange={(e) =>
              onLineItemChange(
                item.id,
                "unitPrice",
                e.target.value === "" ? "" : parseFloat(e.target.value)
              )
            }
            $hasError={fieldHasError(item.unitPrice)}
          />
        </LineItemsCell>
        <LineItemsCell>
          <LineItemSelect
            value={item.unit}
            onChange={(e) => onLineItemChange(item.id, "unit", e.target.value)}
            $hasError={fieldHasError(item.unit)}
          >
            {unitList.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.label)}
              </option>
            ))}
          </LineItemSelect>
        </LineItemsCell>
        <LineItemsCell>
          <Image
            src={deleteIcon}
            alt={t("GENERIC_ACTION_DELETE")}
            title={t("GENERIC_ACTION_DELETE")}
            className="company-centre-view__image-icon-header-company"
            onClick={() => removeLineItem(item.id)}
          />
        </LineItemsCell>
      </LineItemsRow>
      <LineItemsRowCommentCell column={LINE_ITEMS_COLUMNS}>
        {!item.isCommentOpen && (
          <RowCommentLink onClick={() => toggleComment(item.id)}>
            {t("PURCHASE_ORDER_LINE_ADD_COMMENT")}
          </RowCommentLink>
        )}
        {item.isCommentOpen && (
          <RowCommentEditorWrapper>
            <CKEditorText
              descriptionData={item.comment}
              setDescriptionData={(value) => onCommentChange(item.id, value)}
            />
          </RowCommentEditorWrapper>
        )}
      </LineItemsRowCommentCell>
    </React.Fragment>
  );

  const groupedItems = lineItems.filter((item) => item.groupKey);
  const freeItems = lineItems.filter((item) => !item.groupKey);

  const groups = groupedItems.reduce((acc, item) => {
    const key = item.groupKey;
    if (!acc[key]) {
      acc[key] = { groupLabel: item.groupLabel, items: [] };
    }
    acc[key].items.push(item);
    return acc;
  }, {});


  return (
    <LineItemsTableWrapper>
      <LineItemsGrid>
        <LineItemsHeaderRow column={LINE_ITEMS_COLUMNS}>
          <LineItemsHeaderCell>{t("PURCHASE_ORDER_HESUS_SERVICE")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("LABEL")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("PRODUCT")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("TRUCK_TYPE")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("QUANTITY")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("UNIT_PRICE")}</LineItemsHeaderCell>
          <LineItemsHeaderCell>{t("UNIT")}</LineItemsHeaderCell>
          <LineItemsHeaderCell />
        </LineItemsHeaderRow>

        {lineItems.length === 0 && (
          <NoLineItemsMessage>
            {t("PURCHASE_ORDER_NO_LINE_ITEMS")}
          </NoLineItemsMessage>
        )}

        {Object.entries(groups).map(([groupKey, group]) => (
          <React.Fragment key={groupKey}>
            <GroupHeaderBar>{group.groupLabel}</GroupHeaderBar>
            {group.items.map((item) => renderRow(item))}
          </React.Fragment>
        ))}

        {freeItems.length > 0 && (
          <React.Fragment>
            <GroupHeaderBar isFree>
              {t("PURCHASE_ORDER_FREE_LINES")}
            </GroupHeaderBar>
            {freeItems.map((item) => renderFreeRow(item))}
          </React.Fragment>
        )}
      </LineItemsGrid>
    </LineItemsTableWrapper>
  );
};

PurchaseOrderLineItems.propTypes = {
  t: PropTypes.func.isRequired,
  lineItems: PropTypes.array.isRequired,
  setLineItems: PropTypes.func.isRequired,
  errors: PropTypes.object,
  status: PropTypes.number,
};

PurchaseOrderLineItems.defaultProps = {
  errors: {},
  status: 0,
};

export default withTranslation()(PurchaseOrderLineItems);
